// SubscriptionSize.js
import flatstore from 'flatstore';
import parse from 'html-react-parser';
import { isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionPaths, localStorageUtil } from '../../../utils/functions';
import {
    getSubscriptionProductList
} from "../../../utils/services";


function SubscriptionSize({subscription, isResetting}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const paths = getSubscriptionPaths();
    const type = 'customize';

    // Get size from localStorage - apply preset immediately
    const [size, setSize] = useState(() => {
        const subscriptionData = localStorageUtil.getItem('subscription') || {};
        return subscriptionData.size || 'small';
    });
    
    // Sync size with localStorage changes (e.g., from URL presets applied in parent component)
    useEffect(() => {
        const subscriptionData = localStorageUtil.getItem('subscription') || {};
        const sizeFromStorage = subscriptionData.size;
        if (sizeFromStorage && sizeFromStorage !== size) {
            setSize(sizeFromStorage);
        }
    }, [isResetting]); // Re-check when resetting flag changes (URL params applied)

    const [submissionError, setSubmissionError] = useState('');

    const [data, setData] = useState([]);
    const [hasGiftItems, setHasGiftItems] = useState(null); // null = not checked yet

    useEffect(() => {
        if( ! type ){
            navigate(paths.base || '/');
        }
    }, []);

    useEffect(() => {
        if (isNil(subscription)) {
            getSubscriptionProductList();
        }
        if (subscription && !isNil(subscription)) {
            setData(subscription);
            
            // Check if there are any gift items available for the selected size
            const giftItems = subscription.filter((item) => {
                return (
                    item.subscription_type === "grutto_subscription_custom_" + size &&
                    item.is_gift === "yes" &&
                    item.total_stock !== 0
                );
            });
            setHasGiftItems(giftItems.length > 0);
        }
    }, [subscription, size]);

    const handleSubmit = () => {
        if (size) {
            // Get current size from localStorage to check if it's changing
            const subscriptionData = localStorageUtil.getItem('subscription') || {};
            const currentSize = subscriptionData.size;
            
            // If size is changing, reset all items (items are size-specific)
            if (currentSize && currentSize !== size) {
                // Clear all items including gift items when size changes
                localStorageUtil.updateObjectItem('subscription', { 
                    size: size,
                    items: {} // Reset items when size changes
                });
            } else {
                // Size is not changing, just update size
                localStorageUtil.updateObjectItem('subscription', { size: size });
            }
            
            // Check if we should show gift step
            const queryParams = new URLSearchParams(window.location.search);
            const giftSlug = queryParams.get("gift_slug");
            const isResetting = sessionStorage.getItem("resetting_subscription") === "true";
            
            // Determine if we should navigate to gift step:
            // 1. New subscription - Navigate to gift step if gift items exist (or if not checked yet, let GiftSelection handle it)
            // 2. Resetting subscription - ONLY navigate if gift_slug exists in URL (user had gift and it's still valid)
            let shouldShowGift = false;
            if (isResetting) {
                // For resetting subscriptions, only show if gift_slug exists
                shouldShowGift = !!giftSlug;
            } else {
                // For new subscriptions, navigate to gift step if:
                // - Gift items exist (hasGiftItems === true), OR
                // - We haven't checked yet (hasGiftItems === null) - let GiftSelection check and redirect if needed
                // Only skip if we've confirmed no gift items exist (hasGiftItems === false)
                shouldShowGift = hasGiftItems !== false;
            }
            
            if (shouldShowGift) {
                // Preserve query parameters when navigating to gift step
                const giftPath = paths.steps["gift"] || paths.steps[type] || '/';
                navigate(giftPath + window.location.search);
            } else {
                // Skip gift step, go directly to customize
                navigate(paths.steps[type] || '/');
            }
            setSubmissionError('');
        } else {
            setSubmissionError('Please select a size for your subscription.');
        }
    };

    const handleSizeChange = (e) => setSize(e.target.value);

    // Update progress based on selections
    let progressBarWidthPercentage = 0;
    if (size) {
      progressBarWidthPercentage = 25;
    }

    // ProgressBar
    const progressBarWidth = {
        width: `${progressBarWidthPercentage}%`,
    };

    return (
        <div className="subscription-box">

            <div className='progress-bar'>
                <div className='fill' style={progressBarWidth}></div>
            </div>

            {submissionError && (
                <div className="response error">
                {submissionError}
                </div>
            )}

            <div className='container'>
                <div className='row justify-content-center'>
                    <div className='col-md-10'>

                        <div className="container py-4">
                            <div className="row">
                                <div className="col primary-title">
                                    {isResetting ? (
                                        <>
                                            <h2>{t(`subscription.${type}.size-title-update-subscription.title`)}</h2>
                                            <p>{t(`subscription.${type}.size-title-update-subscription.subtitle`)}</p>
                                        </>
                                    ) : (
                                        <>
                                            <h2>{t(`subscription.${type}.size-title.title`)}</h2>
                                            <p>{t(`subscription.${type}.size-title.subtitle`)}</p>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="row justify-content-center">
                            {['small', 'large'].map((sizeOption, index) => (
                                <div key={index} className="col-12 col-md-6">
                                <label className="subscription__select" htmlFor={sizeOption}>
                                    <input
                                    type="radio"
                                    id={sizeOption}
                                    name="size"
                                    value={sizeOption}
                                    checked={size === sizeOption}
                                    onChange={handleSizeChange}
                                    />
                                    <div className="checkbox">
                                    <span></span>
                                    </div>
                                    <div className="body">
                                        <div className="title">
                                            <h3 className="heading">{t(`subscription.${type}.${sizeOption}.title`)}</h3>
                                            <p>{t(`subscription.${type}.${sizeOption}.subtitle`)}</p>
                                            <div className="desc">
                                                {parse(t(`subscription.${type}.size.${sizeOption}`))}
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                </div>
                            ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="subscription__cta">
                <button className="btn btn--red" onClick={handleSubmit}>{t('subscription.'+ type +'.cta')}</button>
            </div>

        </div>
    );
}

export default flatstore.connect(['subscription'])(SubscriptionSize);